import { ID, Logger, MapND, mod, useLogger } from "@kosmod/kit";
import { CanvasCoordinator, CanvasID, ChunkMap, createChunkMap, ReadonlyChunkMap } from "@kosmod/stew";
import { SurfaceID, TerrainCanvas, TerrainCanvasID, TerrainChunk } from "../../defs";
import { Component, h, useMemo, useRef } from "@lukekaalim/act";
import { getChunkDataIndex } from "../../lib/chunks";
import { TerrainSystemData, WalkableNode } from "../TerrainSystems/terrain";
import { DOISTerrain } from "../TerrainSystems/DOISTerrain";
import { TheatreState } from "../../hooks/useTheatreState";
import { SampleTerrainSystem } from "../TerrainSystems/SampleTerrain";
import { Shape } from "@kosmod/pancake";

// export type TerrainSystemDef = {
//   name: string,
//   floorSystemName: string,
//   wallSystemName: string;
// }

// export const GLOBAL_TERRAIN_SYSTEMS = [
//   { name: 'dois', floorSystemName: 'dois_interior_floors', wallSystemName: 'dois_interior_walls' }
// ] as const satisfies TerrainSystemDef[]

/**
 * Exposes a chunkmap interface from an upstream source
 * @param source 
 * @param canvasId 
 * @returns 
 */
export const createProxyChunkMap = (
  source: MapND<[canvas: TerrainCanvasID, x: number, y: number], TerrainChunk>,
  canvasId: TerrainCanvasID,
): ReadonlyChunkMap<number> => {
  return {
    read(x, y) {
      const chunk = source.get([canvasId, Math.floor(x  / 8), Math.floor(y / 8)]);
      if (!chunk)
        return 0;
      const localX = mod(x, 8);
      const localY = mod(y, 8);
      return chunk.data[getChunkDataIndex({ x: 8, y: 8 }, { x: localX, y: localY })];
    },
    canvasId,
    chunks: {
      *keys() {
        for (const [[canvas, x, y]] of source.entries()) {
          if (canvas === canvasId)
            yield [x, y];
        }
      },
    }
  }
}

export type SurfaceRendererProps = {
  canvases: TerrainCanvas[],
  surfaceId: SurfaceID,
  theatre: TheatreState
};

export const SurfaceRenderer: Component<SurfaceRendererProps> = ({ canvases, surfaceId, theatre }) => {
  const activeTool = theatre.tools.getActiveTool()

  const showReal = !activeTool || activeTool.state.type !== 'dev:vis' || activeTool.state.canvas === 'none';
  const debugCanvas = activeTool && activeTool.state.type === 'dev:vis' && activeTool.state.canvas;

  const surfaceData = theatre.surfaceData.get(surfaceId);
  if (!surfaceData)
    return null;

  const canvas = canvases.find(c => c.id === surfaceData.sourceCanvasId);
  if (!canvas)
    return null;

  return [
    showReal && h(DOISTerrain, {
      coordinator: theatre.data.canvasCoordinator,
      theatre,
      terrain: surfaceData.terrain,
      visibility: surfaceData.visibility,
      canvas,
    }),

    debugCanvas === 'interior' && h(SampleTerrainSystem, {
      coordinator: theatre.data.canvasCoordinator, chunkMap: surfaceData.source,
      drawTile: drawInteriorTile
    }),

    debugCanvas === 'neighbors' && h(SampleTerrainSystem, {
      coordinator: theatre.data.canvasCoordinator, chunkMap: surfaceData.terrain,
      resolution: { x: 16, y: 16 },
      drawTile: drawNeighborWalls
    }),
    debugCanvas === 'visibility' && h(SampleTerrainSystem, {
      coordinator: theatre.data.canvasCoordinator, chunkMap: surfaceData.visibility,
      drawTile: drawVisibility
    }),
    debugCanvas === 'walkable' && h(SampleTerrainSystem, {
      coordinator: theatre.data.canvasCoordinator, chunkMap: surfaceData.walkable,
      drawTile: drawWalkable
    }),
  ];
};

// export type SurfaceTerrainRendererProps = {
//   interior: ChunkMap<TerrainSystemData | null>,
//   visibility: ChunkMap<number>,

//   surfaceId: SurfaceID,
//   // system: TerrainSystemDef,
//   theatre: TheatreState
// };
// const SurfaceTerrainRenderer: Component<SurfaceTerrainRendererProps> = ({ theatre, surfaceId }) => {
//   // const logger = useLogger(`SurfaceTerrainRenderer:${surfaceId}:${system.name}`)

//   // first canvas for the surface is the INTERIOR_CANVAS
//   const interiorCanvas = theatre.data.canvases.find(canvas => canvas.surface === surfaceId);
//   if (!interiorCanvas)
//     return null;

//   const interiorChunks = new MapND(theatre.data.chunks.filter(c => c[0] === interiorCanvas.id));
//   const interiorChunkMap = createProxyChunkMap(interiorChunks, interiorCanvas.id);

//   const interior = useTerrainSystem(interiorCanvas, interiorChunkMap, theatre.data.canvasCoordinator);
//   const visibility = useVisibilityMap(interior, theatre.data.minis, theatre.data.canvasCoordinator);
//   // useCalculateWalkableSpace()


//   return 
// }

const drawInteriorTile = (ctx: CanvasRenderingContext2D, x: number, y: number, v: number) => {
  const [floor, wallTop, wallLeft] = Shape.Binary.unpackBytes(v);

  ctx.fillStyle = '#74b88e';
  if (floor) {
    ctx.fillRect(x, y, 3, 3);
  }
  if (wallTop) {
    ctx.fillStyle = 'rgb(221, 188, 3)';
    ctx.fillRect(x, y, 3, 1);
  }
  if (wallLeft) {
    ctx.fillStyle = 'rgb(3, 221, 145)';
    ctx.fillRect(x, y, 1, 3);
  }
  // if (v.wallRight) {
  //   ctx.fillStyle = 'rgb(221, 7, 3)';
  //   ctx.fillRect(x + 2, y, 1, 3);
  // }
  // if (v.wallBottom) {
  //   ctx.fillStyle = 'rgb(10, 3, 221)';
  //   ctx.fillRect(x, y + 2, 3, 1);
  // }
}

const drawVisibility = (ctx: CanvasRenderingContext2D, x: number, y: number, v: number) => {
  ctx.fillStyle = `hsl(0 0% ${(v / 255) * 100}%)`
  ctx.fillRect(x, y, 3, 3);
}

const drawNeighborWalls = (ctx: CanvasRenderingContext2D, x: number, y: number, v: TerrainSystemData | null) => {
  if (!v)
    return;

  ctx.fillStyle = `hsl(${Math.floor(Math.random() * 360)} 50% 50%)`;
  
  ctx.fillRect(x, y, 16, 16);
  ctx.fillStyle = 'rgb(13, 247, 13)';
  ctx.fillRect(x, y, 1, 16);
  ctx.fillStyle = 'rgb(4, 49, 250)';
  ctx.fillRect(x, y, 16, 1);

  if (v.floor) {
    ctx.fillStyle = 'rgb(7, 7, 7)';
    ctx.fillRect(x + 4, y + 4, 8, 8);
  }
  if (v.neighborFloors[0]) {
    ctx.fillStyle = 'rgb(7, 7, 7)';
    ctx.fillRect(x + 7, y + 1, 2, 2);
  }

  if (v.walls[0]) {
    ctx.fillStyle = 'rgb(243, 181, 11)';
    ctx.fillRect(x + 5, y + 4, 6, 2);
  }

  if (v.neighborWalls[0]) {
    ctx.fillStyle = 'rgb(243, 11, 11)';

    if (v.neighborWalls[0][0]) {
      ctx.fillRect(x + 4, y + 0, 2, 4);
    }
    if (v.neighborWalls[0][1]) {
      ctx.fillRect(x + 10, y + 0, 2, 4);
    }
  }
  if (v.neighborWalls[1]) {
    ctx.fillStyle = 'rgb(243, 11, 11)';

    if (v.neighborWalls[1][0]) {
      ctx.fillRect(x + 12, y + 4, 4, 2);
    }
    if (v.neighborWalls[1][1]) {
      ctx.fillRect(x + 12, y + 10, 4, 2);
    }
  }
  if (v.walls[1]) {
    ctx.fillStyle = 'rgb(243, 181, 11)';
    ctx.fillRect(x + 10, y + 5, 2, 6);
  }
  if (v.neighborFloors[1]) {
    ctx.fillStyle = 'rgb(7, 7, 7)';
    ctx.fillRect(x + 13, y + 7, 2, 2);
  }


  if (v.neighborFloors[2]) {
    ctx.fillStyle = 'rgb(7, 7, 7)';
    ctx.fillRect(x + 7, y + 14, 2, 2);
  }

  if (v.neighborWalls[2]) {
    ctx.fillStyle = 'rgb(243, 11, 11)';

    if (v.neighborWalls[2][0]) {
      ctx.fillRect(x + 10, y + 12, 2, 4);
    }
    if (v.neighborWalls[2][1]) {
      ctx.fillRect(x + 4, y + 12, 2, 4);
    }
  }
  if (v.walls[2]) {
    ctx.fillStyle = 'rgb(243, 181, 11)';
    ctx.fillRect(x + 5, y + 10, 6, 2);
  }

  if (v.neighborWalls[3]) {
    ctx.fillStyle = 'rgb(243, 11, 11)';

    if (v.neighborWalls[3][0]) {
      ctx.fillRect(x + 0, y + 10, 4, 2);
    }
    if (v.neighborWalls[3][1]) {
      ctx.fillRect(x + 0, y + 4, 4, 2);
    }
  }
  if (v.walls[3]) {
    ctx.fillStyle = 'rgb(243, 181, 11)';
    ctx.fillRect(x + 4, y + 5, 2, 6);
  }
  if (v.neighborFloors[3]) {
    ctx.fillStyle = 'rgb(7, 7, 7)';
    ctx.fillRect(x + 1, y + 7, 2, 2);
  }
}

const drawWalkable = (ctx: CanvasRenderingContext2D, x: number, y: number, v: WalkableNode | null) => {
  if (!v)
    return;
  if (v.U || v.D || v.L || v.R) {
    ctx.fillStyle = 'rgb(255, 255, 255)';
    ctx.fillRect(x + 1, y + 1, 1, 1);
  }
  if (v.U) {
    ctx.fillStyle = 'rgb(221, 188, 3)';
    ctx.fillRect(x + 1, y, 1, 1);
  }
  if (v.D) {
    ctx.fillStyle = 'rgb(221, 130, 3)';
    ctx.fillRect(x + 1, y + 2, 1, 1);
  }
  if (v.L) {
    ctx.fillStyle = 'rgb(39, 39, 228)';
    ctx.fillRect(x, y + 1, 1, 1);
  }
  if (v.R) {
    ctx.fillStyle = 'rgb(70, 200, 223)';
    ctx.fillRect(x + 2, y + 1, 1, 1);
  }
}
